import { LoyaltyChallengeConfig } from '@shared/models';

import {
  BARCODE_FORMAT,
  DISPLAY_CONDITION,
  OfferType,
  ProductCheckTabType,
  RewardFormat,
  ScanCheckMode,
  SubType,
  TitleType,
  Variants,
} from '../utils';

export const CYGNUS_CONFIG: LoyaltyChallengeConfig = {
  currency: 'USD',
  appIcon: 'assets/images/cygnus/favicon.ico',
  cssVariables: {
    'font-family-regular': 'Overpass-Regular',
    'font-family-light': 'Overpass-Light',
    'font-family-medium': 'Overpass-Medium',
    'font-family-semibold': 'Overpass-Semibold',
    'font-family-bold': 'Overpass-Bold',
    'font-family-black': 'Overpass-Black',
    'font-family-extrabold': 'Overpass-Extrabold',
    'font-family-playfairDisplay-extraBold': 'PlayfairDisplay-Extrabold',

    'navbar-height': '65px',
    'navbar-background-color': '#ffffff',

    'nav-link-color': '#CCCCCC',
    'nav-link-active-color': '#000000',

    'nav-item-bg-color': 'white',
    'nav-item-active-bg-color': 'white',

    'body-color': '#000000',
    'body-background-color': '#ffffff',

    'challenge-primary-color': '#F42836',
    'tooltip-text-color': 'white',
    'tooltip-background-color': 'black',
  },
  rewardFormat: RewardFormat.PRICE,
  og: {
    title: 'ShopRite Challenges | Earn up to $50 in Monthly Rewards',
    description:
      'Activate ShopRite Challenges and earn up to $50 in monthly rewards. Shop your favorite brands with your Price Plus Card and watch your savings grow.',
  },
  rewardTemplate: '{points}<span style="font-size: 14px;">⭐️</span>',
  pages: {
    availablePages: ['current', 'history', 'help'],
    help: {
      meta: {
        title: 'ShopRite Challenges — Help Center & FAQs',
        keywords:
          'loyalty, challenge, current, challenges, loyalty challenge, loyalty challenge current challenges',
        description:
          'Find help with ShopRite Challenges — learn how to activate challenges, earn rewards with your Price Plus Card, and win up to $50 each month.',
      },
      header: {
        goBackButton: {
          text: 'Go Back To My Challenges',
          backgroundColor: '#000000',
          color: 'white',
          fontFamily: 'var(--font-family-bold)',
          borderRadius: '100px',
        },
        background: '#F8C1B8',
        bannerAlternativeText: '',
        howToPlay: {
          specificDisplayCondition: DISPLAY_CONDITION.ALWAYS,
          title: {
            color: 'white',
            text: '',
            fontFamily: 'var(--font-family-extrabold)',
          },
        },
      },
      faqVariant: {
        variant: Variants.VARIANT_1,
        title: {
          displayWhenCampaignBis: false,
          type: TitleType.IMAGE,
          picture: {
            imageUrl: 'assets/images/cygnus/faq-title.svg',
            alternativeText: 'Need Help? Frequently Asked Questions',
          },
        },
        questions: [
          {
            title: 'How do I participate in Price Plus Challenges?',
            content:
              'To participate, open your unique Price Plus Challenges link or visit the Price Plus Challenges hub*online or in the app. Then choose and activate up to 10 Challenges, shop qualifying ShopRite items using the Price Plus Club Card account tied to your ShopRite account, and track your progress during the monthly Challenge period. <br/></br/>How it works: activate your Challenges, shop qualifying items, track progress within 24 hours, then clip and load any earned reward after the monthly Challenge period ends.',
          },
          {
            title: 'How often will I receive new Challenges?',
            content:
              'Price Plus Challenges are offered monthly and typically begin on the first day of the month. You can activate, shop, and earn progress through the expiration date shown on the Challenges page and in related communications.',
          },
          {
            title: 'How many Price Plus Challenges can I activate at one time?',
            content:
              'You can activate up to 10 ShopRite Challenges each month.',
          },
          {
            title: 'How do I earn rewards?',
            content:
              'After you activate a Price Plus Challenge, shop eligible items in store with your Price Plus Club Card account or online while signed in to your ShopRite account. As you make qualifying purchases, your progress increases toward the available reward milestones.',
          },
          {
            title:
              'Which purchases count toward my activated Price Plus Challenges?',
            content:
              'Qualifying ShopRite purchases count when you scan the Price Plus Club Card account tied to your ShopRite account in store or shop online while signed in. Purchases at other banners will not count toward Price Plus Challenges.',
          },
          {
            title:
              'Can Shoppers participate in Price Plus Challenges and redeem rewards at banners other than ShopRite?',
            content:
              ' No. The Challenges program is currently available exclusively to ShopRite customers. Eligible purchases that count toward Challenge progress must be made at participating ShopRite stores, and any rewards earned through a Challenge can only be redeemed at ShopRite. Purchases made at other banners will not contribute to Challenge completion, and Challenge rewards cannot be redeemed outside of ShopRite.',
          },
          {
            title:
              'How soon after I make a qualifying purchase will I see progress?',
            content:
              'Progress will update online or in the app within 24 hours after an in-store purchase is made or an online order is picked up or delivered.',
          },
          {
            title: 'How can I track the progress of my Price Plus Challenges?',
            content:
              'You can view progress on the ShopRite Challenges page. Each activated Challenge has its own progress bar so you can see how close you are to earning each reward.',
          },
          {
            title:
              'Why did my friend or neighbor receive a different Challenge than I did?',
            content:
              'Challenges may vary from customer to customer. Offers are designed to provide a more relevant experience based on factors such as shopping activity, purchase history, and participating brands. Customers who have limited shopping history or don’t have enough information available may receive different or more general offers. Because Challenges are personalized, it’s normal for two customers to see different Challenges and rewards within the program.',
          },
          {
            title: 'How do I make progress on my shopping frequency offer?',
            content:
              'Offer progress is tracked on a per-day basis. To earn progress for the day, you must reach acombined spend of $25 within the same calendar day. You can achieve the $25 minimum in a single in-store or online checkout or by making purchases throughout the day that accumulate to $25. Once you reach the $25 threshold, you&#39;ll receive your offer progress credit for that day. Additional spending above $25 on the same day won&#39;t earn extra visits. Your tracker will reset every day at midnight.',
          },
          {
            title: 'Can I participate in Challenges using the in-store kiosk?',
            content:
              'At this time, Challenges are available only through the ShopRite website and mobile app. Customers can view, track, and participate in Challenges by signing into their ShopRite account online or through the app. We are continually evaluating ways to enhance the customer experience, and additional access options may be considered in the future.',
          },
          {
            title:
              'Can I combine Price Plus Challenges with other discounts or digital coupons?',
            content:
              'Yes, ShopRite Challenges rewards earned and loaded to your account can be combined with other discounts or digital coupons.',
          },
          {
            title:
              'When will my rewards be available in my Price Plus Club Card account?',
            content:
              'Rewards are issued after the monthly Price Plus Challenge period ends, not immediately when a milestone is reached. Once issued, you can view your reward online or in the Price Plus Challenges hub* in the app, then clip and load it to your Price Plus Club Card account before redeeming.',
          },
          {
            title:
              'How do I use the rewards I earn through Price Plus Challenges?',
            content:
              'After your reward is issued, clip and load it to your Price Plus Club Card account. Earned rewards are combined into one basket coupon, which must be used in a single transaction and cannot be split across multiple purchases. The reward will apply to a qualifying ShopRite purchase when you scan your Price Plus Club Card account in store or shop online while signed in. Customers may need to spend at least $1 more than the total reward value for the coupon to deduct, similar to current Customer Care credits. Rewards are only given at the end of the month, no matter if you complete all challenges before the month’s conclusion.',
          },
          {
            title: 'How are my rewards calculated?',
            content:
              'As you purchase eligible products, your progress increases toward each activated Price Plus Challenges. Each time you reach a milestone, you may earn additional rewards until the maximum monthly rewards amount is reached.',
          },
          {
            title:
              'When do my rewards earned through Price Plus Challenges expire?',
            content:
              'Your rewards will expire approximately 14 days after they are added to your account and you are notified via email. <br/><br/><sapn style="font-style: italic;">*** Note: Some of the e-commerce experience mentioned like the Challenges hub or app connection are not live for the internal pilot series. Last Updated 7/31.</span>',
          },
        ],
        accordion: {
          styles: {
            title: {
              color: '#000000',
              fontSize: '14px',
              fontFamily: 'var(--font-family-medium)',
            },
            description: {
              color: '#333333',
              fontSize: '14px',
              margin: '0 1rem',
              fontFamily: 'var(--font-family-light)',
              paddingBottom: '0 !important',
            },
          },
          iconMore: 'assets/images/cygnus/expand-more.svg',
          iconLess: 'assets/images/cygnus/expand-less.svg',
          alternativeTextExpandMore: 'Expand More',
          alternativeTextExpandLess: 'Expand Less',
        },
      },
      extraLinks: [
        {
          title: {
            text: 'Need more help?',
            fontFamily: 'var(--font-family-extrabold)',
          },
          link: {
            color: 'white',
            label: 'Contact us',
            fontFamily: 'var(--font-family-bold)',
            backgroundColor: '#F42836',
            href: 'https://www.shoprite.com/contact-us',
          },
        },
      ],
    },
    currentChallenges: {
      meta: {
        title: 'ShopRite Challenges | Earn up to $50 in Monthly Rewards',
        keywords:
          'loyalty, challenge, current, challenges, loyalty challenge, loyalty challenge current challenges',
        description:
          'Activate ShopRite Challenges and earn up to $50 in rewards each month when you shop your favorite brands.',
      },
      background: 'white',
      howDoesitWork: {
        color: 'white',
        fontFamily: 'var(--font-family-bold)',
        backgroundColor: '#000000',
        containerBackgroundColor: 'white',
        text: 'HOW IT WORKS',
        borderRadius: '100px',
        containerPadding: '2rem 0 0',
        modal: {
          background: '#FFFFFF',
          title: {
            text: 'How it works',
            color: '#000000',
            fontFamily: 'var(--font-family-bold)',
          },
          closeButton: {
            imageUrl: 'assets/images/default/close-button.svg',
            alternativeText: 'Close the tutorial',
            color: 'var(--challenge-primary-color)',
          },
          skipNowButton: {
            text: 'Skip for now',
            color: 'white',
            fontFamily: 'var(--font-family-bold)',
            backgroundColor: '#000000',
            borderRadius: '100px',
          },
        },
      },
      howToPlay: {
        specificDisplayCondition: DISPLAY_CONDITION.DEFAULT,
      },
      templateAfterChallenges: {
        mobile: [
          '<div style="margin: 50px auto 20px;text-align: center;">',
          '<p style="color:#000000;margin-bottom:0;font-family:var(--font-family-bold);font-size:16px;">From {startDate} - {endDate}</p>',
          '<p style="color:#000000;margin:8px 0 0;font-family:var(--font-family-semibold);font-size:14px;">{lastRefreshed}</p>',

          '</div>',
        ],
        tablet: [
          '<div style="margin: 50px auto 20px;text-align: center;">',
          '<p style="color:#000000;margin-bottom:0;font-family:var(--font-family-bold);font-size:18px;">From {startDate} - {endDate}</p>',
          '<p style="color:#000000;margin:8px 0 0;font-family:var(--font-family-semibold);font-size:14px;">{lastRefreshed}</p>',

          '</div>',
        ],
        desktop: [
          '<div style="margin: 50px auto;text-align: center;">',
          '<p style="color:#000000;margin-bottom:0;font-family:var(--font-family-bold);font-size:20px;">From {startDate} - {endDate}</p>',
          '<p style="color:#000000;margin:8px 0 0;font-family:var(--font-family-semibold);font-size:14px;">{lastRefreshed}</p>',
          '</div>',
        ],
      },
      dateFormat: {
        startDate: 'MMMM dd',
        endDate: 'MMMM dd, yyyy',
      },
    },
    pastChallenges: {
      meta: {
        title: 'ShopRite Challenges | Your Price Plus Challenges History',
        keywords:
          'loyalty, challenge, history, past challenges, loyalty challenge, loyalty challenge history',
        description:
          'Review the ShopRite Challenges you have completed and the rewards you earned with your Price Plus Card.',
      },
      banner: {
        visibility: true,
        title: 'Your Price Plus\nChallenges History',
        bannerAriaLabel: 'Your Price Plus Challenges History',
        background: {
          mobile:
            "url('assets/images/cygnus/history-grapes.svg') right 0 top 0 / 80px 59px no-repeat, url('assets/images/cygnus/history-berry.svg') left 0 top 30px / 22px 32px no-repeat, #F9F9F5",
          tablet:
            "url('assets/images/cygnus/history-grapes.svg') right 0 top 0 / 155px 115px no-repeat, url('assets/images/cygnus/history-lemon.svg') left 20px top 0 / 97px 72px no-repeat, url('assets/images/cygnus/history-raspberry.svg') left 40px bottom -5px / 36px 36px no-repeat, url('assets/images/cygnus/history-berry.svg') left 0 top 40px / 25px 36px no-repeat, #F9F9F5",
          desktop:
            "url('assets/images/cygnus/history-grapes.svg') right 79px top 0 / 221px 164px no-repeat, url('assets/images/cygnus/history-lemon.svg') left 181px top 0 / 139px 103px no-repeat, url('assets/images/cygnus/history-raspberry.svg') left 123px bottom -7px / 52px 52px no-repeat, url('assets/images/cygnus/history-berry.svg') left 0 top 50px / 36px 52px no-repeat, #F9F9F5",
        },
        styles: {
          title: {
            margin: '0',
            color: '#000000',
            padding: '0 0 0.57em',
            width: 'fit-content',
            lineHeight: '1.05',
            textAlign: 'center',
            whiteSpace: 'pre-line',
            letterSpacing: '0.02em',
            fontSize: 'clamp(22px, 2.62vw, 33.58px)',
            fontFamily: 'var(--font-family-playfairDisplay-extraBold)',
            background:
              "url('assets/images/cygnus/history-underline.svg') left 0.06em bottom / 5.48em 0.33em no-repeat",
          },
          banner: {
            display: 'flex',
            marginBottom: '2rem',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'clamp(150px, 15.63vw, 200px)',
          },
        },
      },
      accordion: {
        variant: Variants.VARIANT_1,
        iconWidth: '24px',
        iconMore: 'assets/images/cygnus/history-chevron-down.svg',
        iconLess: 'assets/images/cygnus/history-chevron-up.svg',
        alternativeTextExpandMore: 'Show more',
        alternativeTextExpandLess: 'Show less',
        date: {
          color: '#33333F',
          format: 'MMMM d',
          fontFamily: 'var(--font-family-bold)',
          template: '{firstDate} - {lastDate}',
        },
        reward: {
          color: '#F42836',
          fontFamily: 'var(--font-family-bold)',
        },
      },
      noChallengesText: {
        text: 'No selected challenges',
        color: '#33333F',
        fontFamily: 'var(--font-family-semibold)',
      },
      noRewardsText: {
        text: 'No rewards earned',
        color: '#33333F',
        fontFamily: 'var(--font-family-semibold)',
      },
    },
    sorry: {
      background:
        'white url("assets/images/cygnus/cover-sorry-page.webp") center center / cover no-repeat',
      content: [
        '<div style="text-align: center; font-size: 1.5rem;color: black; font-family: var(--font-family-bold);">Oops!</div>',
        '<div style="text-align: center; font-size: 1.5rem;color: black; font-family: var(--font-family-bold);">This site is temporarily unavailable.</div>',
      ],
    },
  },
  components: {
    header: {
      variant: Variants.VARIANT_13,
      backgroundColor: '#F9F9F5',
      bannerAriaLabel:
        'ShopRite Loyalty: earn up to $50 with your favorite brands',
      backgroundImage: {
        mobile: {
          imageUrl: 'assets/images/cygnus/cover-mobile.webp',
        },
        tablet: {
          imageUrl: 'assets/images/cygnus/cover-tablet.webp',
        },
        desktop: {
          imageUrl: 'assets/images/cygnus/cover-desktop.webp',
        },
      },
      campaignDate: {
        color: '#000000',
        fontFamily: 'var(--font-family-bold)',
        template: 'From {startDate} - {endDate}',
      },
      shopRiteLogo: {
        imageUrl: 'assets/images/cygnus/shop-rite-logo.svg',
        alternativeText: 'ShopRite Challenges',
      },
      rewardLogo: {
        imageUrl: 'assets/images/cygnus/reward-logo.svg',
        alternativeText: 'Earn upto $50 Per Month',
      },
      additionalInfo: {
        mobileText:
          'Shop your favorite brands\n and earn Rewards up to $50 per month in rewards!',
        desktopText: 'Shop your favorite brands & earn rewards!',
        color: '#000000',
        fontFamily: 'var(--font-family-bold)',
      },
    },
    brands: {
      variant: Variants.VARIANT_3,
      backgroundColor: '',
      brandsBgColor: 'white',
      border: '1.5px solid #EFEFEF',
      label: {
        text: 'Shop your favorite brands\n & win rewards',
        color: '#000000',
        fontFamily: 'var(--font-family-semibold)',
        textTransform: 'none',
      },
    },
    footer: {
      variant: Variants.VARIANT_1,
      mobileVisiblity: true,
      styles: {
        display: 'flex',
        padding: '30px 0',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        backgroundColor: '#000000',
      },
      linkStyles: {
        color: 'white',
        fontSize: '16px',
        textDecoration: 'none',
        fontFamily: 'var(--font-family-extrabold)',
      },
      links: [
        {
          url: 'help',
          label: 'Help Center',
        },
        {
          url: 'https://www.shoprite.com/terms-conditions',
          external: true,
          label: 'Terms and Conditions',
        },
        {
          url: 'https://www.shoprite.com/privacy-policy',
          external: true,
          label: 'Privacy Notice',
        },
      ],
    },
    tutorial: {
      background: '#F8C1B8',
      carousel: {
        config: {
          sectionAriaLabel: 'Tutorial carousel',
          controlsAriaLabel: 'Carousel controls',
          paginationAriaLabel: 'Slide indicators',
          title: {
            fontSize: '18px',
            color: '#000000',
            text: 'HOW IT WORKS',
            fontFamily: 'var(--font-family-extrabold)',
          },
          dots: {
            activeBg: '#000000',
            inactiveBg: '#00000073',
            ariaLabel: 'Go to slide {variable} of {variable}',
          },
          slideIndicatorLabel: 'Slide {variable} on {variable}',
          controls: {
            leftIcon: {
              imageUrl: 'assets/images/cygnus/chevron-left.svg',
              alternativeText: 'Go to the previous slide',
            },
            rightIcon: {
              imageUrl: 'assets/images/cygnus/chevron-right.svg',
              alternativeText: 'Go to the next slide',
            },
          },
        },
        items: {
          variant: Variants.VARIANT_1,
          styles: {
            title: {
              color: '#4A4A4A',
              fontFamily: 'var(--font-family-extrabold)',
            },
            description: {
              color: '#4A4A4A',
              fontFamily: 'var(--font-family-medium)',
            },
            card: {
              border: 'none',
              boxShadow: 'none',
              background: '#FFFFFF',
              borderRadius: '10px',
            },
          },
          steps: [
            {
              title: 'ACTIVATE',
              description: 'Select up to 10 challenges per month.',
              picture: {
                imageUrl: 'assets/images/cygnus/step-1.svg',
                alternativeText: 'Select up to 10 challenges per month.',
              },
            },
            {
              title: 'SHOP',
              description:
                'Scan your Price Plus Club Card in-store or log in when shopping online.',
              picture: {
                imageUrl: 'assets/images/cygnus/step-2.svg',
                alternativeText:
                  'Scan your Price Plus Club Card in-store or log in when shopping online.',
              },
            },
            {
              title: 'EARN',
              description:
                'Shop your favorite brands to earn up to $50 in rewards per month.',
              picture: {
                imageUrl: 'assets/images/cygnus/step-3.svg',
                alternativeText:
                  'Shop your favorite brands to earn up to $50 in rewards per month.',
              },
            },
          ],
        },
      },
    },
    wallet: {
      variant: Variants.VARIANT_2,
      backgroundColor: '#F42836',
      label: {
        color: 'white',
        fontSize: '14px',
        text: 'Rewards Earned',
        fontFamily: 'var(--font-family-medium)',
      },
      amount: {
        color: 'white',
        fontFamily: 'var(--font-family-extrabold)',
      },
    },
    navbar: {
      variant: Variants.VARIANT_1,
      menuVariant: Variants.VARIANT_2,
      hasLayoutPaddingTop: true,
      dropdownLabel: 'Menu',
      openButton: {
        imageUrl: 'assets/images/cygnus/menu-icon.svg',
        imageAlt: 'Click to see more options',
        buttonTitle: 'Click to see more options',
      },
      fontFamily: 'var(--font-family-bold)',
      logo: {
        url: 'current',
        height: '35px',
        imageUrl: 'assets/images/cygnus/retailer-logo.webp',
        alternativeText: 'ShopRite Logo',
      },
      items: [
        {
          url: 'current',
          label: 'My Challenges',
          ariaLabel: 'My Current Challenges',
          menuIcon: {
            imageUrl: 'assets/images/cygnus/home.svg',
            alternativeText: 'Home Menu Icon',
          },
          iconUrlSelected: {
            imageUrl: 'assets/images/cygnus/home-selected.svg',
            alternativeText: 'Home Menu Icon Selected',
          },
        },
        {
          url: 'history',
          label: 'History',
          ariaLabel: 'My Past Challenges',
          menuIcon: {
            imageUrl: 'assets/images/cygnus/history.svg',
            alternativeText: 'History Menu Icon',
          },
          iconUrlSelected: {
            imageUrl: 'assets/images/cygnus/history-selected.svg',
            alternativeText: 'History Menu Icon Selected',
          },
        },
        {
          url: 'help',
          label: 'FAQs',
          ariaLabel: '',
          menuIcon: {
            imageUrl: 'assets/images/cygnus/help.svg',
            alternativeText: 'Help Menu Icon',
          },
          iconUrlSelected: {
            imageUrl: 'assets/images/cygnus/help-selected.svg',
            alternativeText: 'Help Menu Icon Selected',
          },
        },
      ],
    },
    countdown: {
      variant: Variants.VARIANT_1,
      showOnNavbarWhen: [],
      showAfterCampaignSummaryWhen: [],
      showBeforeCampaignSummaryWhen: [],
      showOnPages: 'all',
      titleAppearing: ['row', 'row', 'row'],
      titleVerticalAlignment: ['center', 'center', 'center'],
      labelStyle: {
        color: '#000000',
        fontFamily: 'var(--font-family-semibold)',
      },
      title: {
        color: '#000000',
        showTitleOnMobile: true,
        fontFamily: 'var(--font-family-semibold)',
        label: 'Challenges ends in',
      },
      numberStyle: {
        color: 'white',
        backgroundColor: '#F42836',
        fontFamily: 'var(--font-family-extrabold)',
      },
      texts: {
        days: 'days',
        hours: 'hours',
        minutes: 'minutes',
        seconds: 'seconds',
      },
    },
    challenges: {
      elements: {
        winCard: {
          variant: Variants.VARIANT_4,
          splitCurrencyAmount: true,
          backgroundColor: '#F8C1B8',
          left: {
            backgroundImage: {
              imageUrl: 'assets/images/cygnus/win-reward.svg',
              alternativeText:
                'You earned {variable}€ by completing this challenge',
            },
            amount: {
              color: '#000000',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          right: {
            winFirstLabel: {
              text: 'Challenge',
              color: '#000000',
              fontFamily: 'var(--font-family-extrabold)',
            },
            winSecondLabel: {
              text: 'Completed',
              color: '#000000',
              fontFamily: 'var(--font-family-extrabold)',
            },
            picture: {
              imageUrl: 'assets/images/cygnus/wincard-vector.svg',
              alternativeText: '',
            },
          },
        },
        selectionButton: {
          variant: Variants.VARIANT_3,
          splitCurrencyAmount: true,
          buttonCatchPhrase: {
            buttonLabel: 'Activate',
            buttonBackgroundColor: '#000000',
            buttonColor: 'white',
            buttonBorderRadius: '100px',
            buttonFontFamily: 'var(--font-family-bold)',
          },
          catchPhrase: {
            selectImage: {
              imageUrl: 'assets/images/cygnus/step-reward-container.svg',
              imageUrlByType: {
                [OfferType.FREQUENCY]:
                  'assets/images/cygnus/frequency-reward-container.svg',
              },
              alternativeText:
                'Select this Challenge and win up to ${variable}',
            },
            amountFontFamily: 'var(--font-family-bold)',
            amountColor: '#000000',
          },
        },
        unselectionButton: {
          variant: Variants.VARIANT_2,
          text: 'Deactivate',
          title: 'Deactivate this challenge',
          color: '#FF0000',
          fontFamily: 'var(--font-family-semibold)',
        },
        outOfStock: {
          variant: Variants.VARIANT_1,
          backgroundColor: 'rgba(248, 193, 184, 0.65)',
          borderRadius: '16px',
          banner: {
            textColor: 'white',
            borderRadius: '4px',
            backgroundColor: '#F42836',
            text: 'Challenge Full',
            fontFamily: 'var(--font-family-extrabold)',
          },
        },
      },
      step: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_6,
          splitCurrencyAmount: true,
          bannerAlternativeText: 'Challenges Banner',
          logoAlternativeText: 'Logo banner',
          header: {
            height: '143px',
            backgroundColor: '#FFFFFF',
          },
          subHeader: {
            color: '#242729',
            backgroundColor: 'white',
            completedBackgroundColor: '#F8C1B8',
            fontFamily: 'var(--font-family-semibold)',
            fontSize: '12px',
          },
          body: {
            progressbar: {
              pictureStageReached: {
                imageUrl: 'assets/images/cygnus/picture-reached.svg',
                alternativeText: 'Reached reward',
                width: '25px',
              },
              earnLabel: 'Earn',
              spendLabel: 'Spend',
              labelColor: '#000000',
              progressColor: '#F42836',
              containerColor: '#D5E1EC',
              labelFontFamily: 'var(--font-family-bold)',
            },
            rewards: {
              rewardFontFamily: 'var(--font-family-bold)',
              showReachedIconInBubble: false,
              backgroundColors: {
                bubbleWidth: '65px',
                bubbleHeight: '54px',
                backgroundImage: 'assets/images/cygnus/max-reward.svg',
                default: {
                  fill: 'white',
                  stroke: '#F42836',
                  rewardColor: 'white',
                },
                reached: {
                  fill: '#F42836',
                  stroke: '#F42836',
                  rewardColor: 'white',
                },
                unreached: {
                  fill: 'white',
                  stroke: '#000000',
                  rewardColor: '#000000',
                },
                alternativeText: {
                  reached: 'I earned ${variable}',
                  default: 'I earned ${variable}',
                  unreached: 'Earn ${variable}',
                },
              },
            },
            targets: {
              color: '#242729',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          footer: {
            backgroundColor: 'white',
            unselectLabel: {
              color: '#FF0000',
              fontFamily: 'var(--font-family-semibold)',
              label: 'Deactivate',
            },
            totalSpent: {
              color: '#002E3B',
              fontFamily: 'var(--font-family-regular)',
              label: 'Progress',
              spentAmountFontFamily: 'var(--font-family-bold)',
            },
            eligibleProductLabel: {
              color: '#000000',
              fontFamily: 'var(--font-family-semibold)',
              label: 'Eligible products',
            },
          },
        },
      },
      frequency: {
        [SubType.DEFAULT]: {
          variant: Variants.VARIANT_12,
          splitCurrencyAmount: true,
          visitsCounter: {
            progress: {
              color: '#000000',
              fontFamily: 'var(--font-family-bold)',
              imageUrl: 'assets/images/cygnus/frequency-stamp-empty.svg',
            },
            target: {
              color: 'white',
              backgroundColor: '#000000',
              fontFamily: 'var(--font-family-bold)',
            },
          },
          boxShadow: '0 4px 24px rgba(51, 51, 51, 0.18)',
          header: {
            backgroundImage: {
              url: 'assets/images/cygnus/frequency-banner.webp',
              bannerAriaLabel: 'Frequency Challenge Banner',
              title: {
                text: 'Shop {variable} times',
                color: '#000000',
                fontFamily: 'var(--font-family-playfairDisplay-extraBold)',
              },
              subTitle: {
                text: 'in-store or online',
                color: '#000000',
                fontFamily: 'var(--font-family-regular)',
              },
            },
            height: '143px',
          },
          subHeader: {
            backgroundColor: 'white',
            completedBackgroundColor: '#F8C1B8',
            title: {
              color: 'black',
              label: 'Minimum spend per visit of ${variable}',
              fontFamily: 'var(--font-family-semibold)',
            },
          },
          body: {
            stamps: {
              showNumbers: true,
              iconSelected: 'assets/images/cygnus/frequency-stamp-success.svg',
              iconNonSelected: 'assets/images/cygnus/frequency-stamp-empty.svg',
            },
            rewardRatio: {
              rewardPicture: {
                imageUrl: 'assets/images/cygnus/frequency-reward-ratio.svg',
                alternativeText: '',
              },
              fontFamily: 'var(--font-family-semibold)',
              color: '#000000',
            },
          },
          footer: {
            color: '#000000',
            label: 'Progress',
            fontFamily: 'var(--font-family-semibold)',
            remainingVisitsLabel: {
              color: '#000000',
              text: 'Only {variable} more {visit|visits} to go.',
              fontFamily: 'var(--font-family-bold)',
            },
          },
        },
      },
    },
    challengeSummary: {
      variant: Variants.VARIANT_1,
      stackOnMobile: true,
      picture: {
        imageUrl: 'assets/images/cygnus/my-challenges.svg',
        alternativeText: 'My Challenges',
      },
      update: {
        color: '#000000',
        text: 'Last Refreshed: {variable}',
        defaultFontSize: '12px',
        fontFamily: 'var(--font-family-semibold)',
        dateFormat: 'MM/dd/yyyy hh:mm a',
      },
      challengeSelection: {
        text: 'Select up to 10 offers and start Earning Rewards!',
        color: '#000000',
        defaultFontSize: '13px',
        desktopFontSize: '20px',
        fontFamily: 'var(--font-family-semibold)',
      },
      creditInfoMsg: {
        text: 'Complete your goals and receive rewards within 4 days after the promotion ends.',
        color: '#000000',
        defaultFontSize: '12px',
        fontFamily: 'var(--font-family-regular)',
      },
      challengeLabels: {
        inProgress: {
          color: '#000000',
          text: 'In progress',
          fontFamily: 'var(--font-family-semibold)',
        },
        onGoingLabel: {
          color: '#000000',
          text: 'Activated',
          fontFamily: 'var(--font-family-semibold)',
        },
        completedLabel: {
          color: '#000000',
          text: 'Completed',
          fontFamily: 'var(--font-family-semibold)',
        },
      },
      counterBorderRadius: '5px',
      challengesInfos: {
        color: '#000000',
        backgroundColor: '#F8C1B8',
        fontFamily: 'var(--font-family-black)',
      },
    },
    modals: {
      closeButton: {
        label: 'Close',
        textColor: 'white',
        borderRadius: '4px',
        backgroundColor: '#F42836',
        fontFamily: 'var(--font-family-bold)',
        ariaLabel: 'Close this Dialog',
      },
      unselectChallenge: {
        backgroundColor: '#FFFFFF',
        header: {
          backgroundColor: '',
          backgroundImage: '',
        },
        frequencyHeaderImages: {
          [SubType.DEFAULT]: {
            imageUrl: 'assets/images/cygnus/frequency-header.webp',
            alternativeText: 'Frequency Challenge in-store or online',
          },
        },
        title: {
          color: '#333333',
          fontFamily: 'var(--font-family-bold)',
          label: 'Are you sure you want to unselect this challenge?',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label: 'Don’t worry. If you regret it you can select it again !',
        },

        actionButtons: {
          variant: Variants.VARIANT_1,
          confirmButton: {
            label: 'Yes',
            borderRadius: '4px',
            textColor: '#FFFFFF',
            backgroundColor: '#F42836',
            fontFamily: 'var(--font-family-bold)',
            ariaLabel: 'Yes, Cancel this challenges',
          },
          closeButton: {
            label: 'No',
            borderRadius: '4px',
            textColor: '#F42836',
            backgroundColor: 'white',
            borderColor: '#F42836',
            fontFamily: 'var(--font-family-bold)',
            ariaLabel: 'No',
          },
        },
      },
      productCheck: {
        ariaLabel: '',
        backgroundColor: '#FFFFFF',
        fontFamily: 'var(--font-family-regular)',
        tabs: [
          {
            type: ProductCheckTabType.SCAN,
            checkMode: ScanCheckMode.PRODUCT_LIST,
            label: 'Scan',
            title: 'Barcode Verification',
            // Wakefern/ShopRite is a US retailer — products use UPC-A barcodes.
            readers: [BARCODE_FORMAT.UPC_A],
            successMessage: 'PRODUCT ELIGIBLE',
            errorMessage: 'PRODUCT NOT ELIGIBLE',
            startButtonLabel: 'Start scanner',
            retryPermissionButtonLabel: 'Retry camera access',
            failureMessage: 'AN ERROR OCCURRED, PLEASE TRY AGAIN',
            restartButtonLabel: 'Restart Scan',
            errorWhenCameraPermissionDenied:
              'Please allow camera access to use the scanner.',
            errorWhenCameraNotFound: 'Camera not available',
            errorWhenCameraBusy:
              'The camera is in use by another app. Close it, then try again.',
            previewLabel: 'Barcode scanner preview',
            subtitle: 'Scan your product here',
          },
          {
            type: ProductCheckTabType.PRODUCT_LIST,
            failureMessage: 'AN ERROR OCCURRED, PLEASE TRY AGAIN',
            label: 'Enter manually',
            searchable: true,
            inputPlaceholder: 'Enter a barcode to search',
            tableTitle: {
              text: 'List of eligible products',
              color: 'var(--challenge-primary-color)',
            },
            columns: {
              name: {
                header: 'Product',
                color: '#000000',
                fontFamily: 'var(--font-family-regular)',
              },
              code: {
                header: 'Barcode',
                color: '#000000',
                fontFamily: 'var(--font-family-regular)',
              },
            },
          },
        ],
        tabConfig: {
          activeColor: 'white',
          inactiveColor: 'black',
          inactiveBackground: 'white',
          fontFamily: 'var(--font-family)',
          inactiveBorderBottom: '2px solid #e6e6e6',
          activeBackground: 'var(--challenge-primary-color)',
          activeBorderBottom: '2px solid var(--challenge-primary-color)',
        },
      },
      firstSelection: {
        backgroundColor: 'white',
        title: {
          color: '#333333',
          label: 'Challenge selected',
          fontFamily: 'var(--font-family-bold)',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/cygnus/first-selection.svg',
            alternativeText: 'You have selected the first challenge',
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label: 'Nice choice! You can pick another 9 Challenges.',
        },
      },
      lastSelection: {
        backgroundColor: 'white',
        title: {
          color: '#333333',
          fontFamily: 'var(--font-family-bold)',
          label: "That's {variable} challenges",
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/cygnus/last-selection.svg',
            alternativeText: "That's {variable} challenges Selected",
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            'Simply shop in-store or online to hit your targets and collect up to $50.',
        },
      },
      maximumSelection: {
        backgroundColor: 'white',
        title: {
          color: '#333333',
          fontFamily: 'var(--font-family-bold)',
          label: 'Sorry, you have already selected {variable} challenges',
        },
        header: {
          backgroundImage: '',
          backgroundColor: 'white',
          logo: {
            imageUrl: 'assets/images/cygnus/maximum-selection.svg',
            alternativeText:
              'Sorry, you have already selected {variable} challenges',
          },
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            'Don’t worry. You can deselect an unstarted Challenge to pick another Challenge.',
        },
      },
      outOfStockIssue: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Limit reached',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            'This Challenge has reached the maximum number of participants. Please choose another.',
        },
      },
      genericError: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Error',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            "An error occurred, and the Challenge couldn't be selected. Please check your connection and try again.",
        },
      },
      timeoutError: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Request timed out',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            'Selecting the Challenge took too long. Please check your connection and try again.',
        },
      },
      challengeUnavailable: {
        backgroundColor: 'white',
        title: {
          color: '#272f35',
          fontFamily: 'var(--font-family-bold)',
          label: 'Campaign ended',
        },
        body: {
          color: '#000000',
          fontFamily: 'var(--font-family-medium)',
          label:
            'This campaign has ended and Challenges are no longer available for selection.',
        },
      },
    },
  },
};
